/**
 * DataSource detail page
 *
 * Reads the source's domain from the URL path and fetches it from Payload CMS
 * (data-sources collection; DataSource is not a graph type, electinfo/rundeck#914).
 * Renders source details including name, description, external link, and data types.
 */

const CMS_ENDPOINT = '/api/cms/data-sources';

interface DataSourceDoc {
  id: string;
  name: string;
  domain?: string | null;
  url?: string | null;
  short_description?: string | null;
  data_types?: string[] | null;
}

/** CMS select values are slugs ("zip-codes"); show them as words ("ZIP Codes"). */
function dataTypeLabel(value: string): string {
  return value
    .split('-')
    .map(w => (w === 'zip' ? 'ZIP' : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getDataSourceId(): string | null {
  // URL: /datasources/{id}/ → extract id
  const parts = window.location.pathname.replace(/\/$/, '').split('/');
  // ["", "datasources", "{id}"]
  if (parts.length >= 3 && parts[1] === 'datasources') {
    return decodeURIComponent(parts[2]);
  }
  return null;
}

async function loadDataSource(id: string): Promise<void> {
  const container = document.getElementById('datasource-detail');
  if (!container) return;

  const params = new URLSearchParams({
    'where[domain][equals]': id,
    'where[cms_status][equals]': 'published',
    limit: '1',
  });

  try {
    const res = await fetch(`${CMS_ENDPOINT}?${params}`);

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const { docs } = await res.json();
    const sources: DataSourceDoc[] = docs || [];
    if (!sources.length) {
      container.innerHTML = '<p class="error">Data source not found.</p>';
      return;
    }

    renderDataSource(container, sources[0]);
  } catch (err) {
    container.innerHTML = `<p class="error">Failed to load data source: ${(err as Error).message}</p>`;
    console.error(err);
  }
}

function renderDataSource(container: HTMLElement, ds: DataSourceDoc): void {
  // Update page title
  document.title = `${ds.name} - Data Sources - US Elections`;

  // Update header
  const headerName = document.getElementById('ds-name');
  const headerDomain = document.getElementById('ds-domain');
  if (headerName) headerName.textContent = ds.name;
  if (headerDomain) headerDomain.textContent = ds.domain || '';

  const url = ds.url || '';
  const dataTypes = (ds.data_types || []).map(dt =>
    `<span class="data-type-tag">${escapeHtml(dataTypeLabel(dt))}</span>`
  ).join('');

  container.innerHTML = `
    <div class="ds-section">
      <p class="ds-description">${escapeHtml(ds.short_description || '')}</p>
    </div>

    <div class="ds-section">
      <h2>External Link</h2>
      <a href="${escapeHtml(url)}" target="_blank" rel="noopener" class="ds-external-link">
        ${escapeHtml(url)} &rarr;
      </a>
    </div>

    <div class="ds-section">
      <h2>Data Types</h2>
      <div class="ds-data-types">${dataTypes}</div>
    </div>
  `;
}

function init(): void {
  const id = getDataSourceId();
  if (!id) {
    const container = document.getElementById('datasource-detail');
    if (container) {
      container.innerHTML = '<p class="error">No data source domain found in URL.</p>';
    }
    return;
  }
  loadDataSource(id);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
